import {
  Button,
  Form,
  Input,
  Drawer,
  Select,
  Tooltip,
  Space,
  InputNumber,
  Radio,
  Table,
  Row,
  Col,
  Spin,
} from "antd";
import React, { useRef, useState, useEffect } from "react";
import ItemsService, {
  UnitTypesOption,
  UnitTypes,
  UnitCategory,
  UnitCategoryOptions,
  ICompositeUnitRequest,
  IUnitRequest,
  IUnitListResponse,
} from "../../Services/ItemsService";
import { useClient } from "../../Context/ClientContext";
import { notificationContext } from "../../Common/Routing/PageRoute";

import EntryLevelKeys from "../../Common/EntryLevelKeys";
import { useHotkey } from "../../Context/HotKeyContext";
import {
  CloseOutlined,
  QuestionCircleOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { resolve } from "path";
import { rejects } from "assert";
import { IApiResponse } from "../../../Utilities/ApiUtility.axios";

const UnitModel: React.FC<{
  open: boolean;
  data: IUnitRequest | null;
  setTrigger: React.Dispatch<React.SetStateAction<boolean>>;
  onCancel: React.Dispatch<React.SetStateAction<boolean>>;
  setAddUnitVal?: React.Dispatch<
    React.SetStateAction<IUnitRequest | undefined>
  >;
}> = (props) => {
  const { showKeys } = useHotkey();
  const autoCompleteRef = useRef<any>(null);
  const [form] = Form.useForm<IUnitRequest>();
  const { currentClient } = useClient();
  const { openNotification } = React.useContext(notificationContext);
  const [saveButtonLoading, setSaveButtonLoading] =
    React.useState<boolean>(false);

  const [selectedType, setSelectedType] = useState<UnitTypes | null>(null);
  const [loadingChildCompoundUnits, setLoadingChildCompoundUnits] =
    useState<boolean>(false);
  const [unitCategory, setUnitCategory] = useState<UnitCategory>(
    UnitCategory.Simple
  );

  const [compositeUnits, setCompositeUnits] = useState<ICompositeUnitRequest[]>(
    []
  );
  const [availableUnits, setAvailableUnits] = useState<IUnitListResponse[]>([]);

  const fetchAllUnits = async () => {
    setLoadingChildCompoundUnits(true);
    try {
      const response = await ItemsService.getUnitsList(
        currentClient?.id!,
        0,
        0,
        "",
        "",
        "",
        0,
        null
      );
      if (response?.result?.items) {
        const filteredUnits = response?.result?.items.filter(
          (unit: IUnitRequest) => unit.id !== props.data?.id
        );
        // setChildCompoundUnits(filteredUnits);
        setAvailableUnits(filteredUnits);
      }
    } catch (error) {
      console.error("Error fetching parent units:", error);
      openNotification("error", "Failed to load child compound units");
    } finally {
      setLoadingChildCompoundUnits(false);
    }
  };

  const handleUnitNameDuplicate = (val: string) =>
    availableUnits.some(
      (u) =>
        u.name.toLocaleLowerCase().trim() === val.toLocaleLowerCase().trim()
    );
  const handleUnitShortNameDuplicate = (val: string) =>
    availableUnits.some(
      (u) =>
        u.shortName.toLocaleLowerCase().trim() ===
        val.toLocaleLowerCase().trim()
    );
  //   const fetchChildCompoundUnits = async (unitType: UnitTypes) => {
  //     if (unitType === null || unitType === undefined) return;

  //     setLoadingChildCompoundUnits(true);
  //     try {
  //       const response = await ItemsService.getChildCompoundList(
  //         currentClient?.id!,
  //         unitType
  //       );
  //       if (response?.items) {
  //         const filteredUnits = response.items.filter(
  //           (unit: IUnitRequest) => unit.id !== props.data?.id
  //         );
  //         // setChildCompoundUnits(filteredUnits);
  //         setAvailableUnits(filteredUnits);
  //       }
  //     } catch (error) {
  //       console.error("Error fetching parent units:", error);
  //       openNotification("error", "Failed to load child compound units");
  //     } finally {
  //       setLoadingChildCompoundUnits(false);
  //     }
  //   };

  const handleTypeChange = (value: UnitTypes) => {
    setSelectedType(value);
    setCompositeUnits([]);
  };

  const handleCategoryChange = (value: UnitCategory) => {
    setUnitCategory(value);
    if (value === UnitCategory.Simple) {
      setCompositeUnits([]);
    }
  };

  const addCompositeUnit = () => {
    setCompositeUnits([
      ...compositeUnits,
      {
        unitId: 0,
        unitName: "",
        shortName: "",
        quantity: 1,
        order: compositeUnits.length + 1,
        decimalPlaces: 2,
      },
    ]);
  };

  const removeCompositeUnit = (index: number) => {
    const newUnits = compositeUnits.filter((_, i) => i !== index);
    const reordered = newUnits.map((unit, idx) => ({
      ...unit,
      order: idx + 1,
    }));
    setCompositeUnits(reordered);
  };

  const updateCompositeUnit = (index: number, field: string, value: any) => {
    const newUnits = [...compositeUnits];

    if (field === "unitId") {
      const selectedUnit = availableUnits.find((u) => u.id === value);
      if (selectedUnit) {
        newUnits[index] = {
          ...newUnits[index],
          unitId: value,
          unitName: selectedUnit.name,
          shortName: selectedUnit.shortName,
          decimalPlaces: selectedUnit.decimalPlaces ?? 2,
        };
      }
    } else {
      newUnits[index] = { ...newUnits[index], [field]: value };
    }
    setCompositeUnits(newUnits);
  };

  const compositeColumns = [
    {
      title: "Order",
      dataIndex: "order",
      width: "15%",
      render: (_: any, record: any, index: number) => index + 1,
    },
    {
      title: "Unit",
      dataIndex: "unitId",
      width: "40%",
      render: (_: any, record: any, index: number) => (
        <Select
          placeholder="Select unit"
          value={record.unitId || undefined}
          onChange={(value) => updateCompositeUnit(index, "unitId", value)}
          style={{ width: "100%" }}
          options={availableUnits
            .filter((u) => u.type === selectedType)
            .filter(
              (u) =>
                !compositeUnits.some(
                  (cu, idx) => idx !== index && cu.unitId === u.id
                )
            )
            .map((unit) => ({
              value: unit.id,
              label: `${unit.name} (${unit.shortName})`,
            }))}
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      ),
    },
    {
      title: () => (
        <>
          Conversion Value
          <Tooltip
            title="Quantity precision depends on selected unit's decimal places (0-6)"
            placement="top"
            color="#4b5b95"
          >
            <QuestionCircleOutlined style={{ marginLeft: "3px" }} />
          </Tooltip>
        </>
      ),
      dataIndex: "quantity",
      width: "35%",
      render: (_: any, record: any, index: number) => {
        return (
          <InputNumber
            min={0.000001}
            max={999999}
            value={record.quantity}
            onChange={(value) => updateCompositeUnit(index, "quantity", value)}
            style={{ width: "100%" }}
            disabled={!record.unitId}
            placeholder="Enter conversion value"
          />
        );
      },
    },
    {
      title: "Action",
      width: "10%",
      render: (_: any, record: any, index: number) => (
        <DeleteOutlined
          style={{ color: "red", cursor: "pointer" }}
          onClick={() => removeCompositeUnit(index)}
        />
      ),
    },
  ];

  const postItem = () => {
    form.validateFields().then(async (value: any) => {
      if (unitCategory === UnitCategory.Compound) {
        if (compositeUnits.length === 0) {
          openNotification(
            "error",
            "Please add at least one unit for compound unit"
          );
          return;
        }

        if (
          compositeUnits.length > 0 &&
          compositeUnits.some((u) => u.quantity === 0)
        ) {
          openNotification(
            "error",
            "Please check the given conversion value, 0 must not be there"
          );
          return;
        }
      }

      setSaveButtonLoading(true);

      let data: IUnitRequest = {
        name: value.name,
        shortName: value.shortName,
        type: value.type,
        decimalPlaces: Number(value.decimalPlaces),
        compositeUnits:
          unitCategory === UnitCategory.Compound ? compositeUnits : null,
        unitCategory: unitCategory,
      };

      try {
        let res: IApiResponse<IUnitRequest>;
        if (props.data?.id) {
          res = await ItemsService.updateUnit(
            data,
            currentClient?.id!,
            props.data.id
          );
        } else {
          res = await ItemsService.addUnit(data, currentClient?.id!);
        }

        if (res?.result) {
          props?.setAddUnitVal && props?.setAddUnitVal(res?.result);
          openNotification(
            "success",
            `Unit ${
              props.data?.id ? "updated successfully." : "added successfully."
            }`
          );
          props.onCancel(false);
          setSaveButtonLoading(false);
          props.setTrigger((x: boolean) => !x);
        } else {
          setSaveButtonLoading(false);
          openNotification("error", res.message);
        }
      } catch (ex: any) {
        setSaveButtonLoading(false);
        openNotification("error", ex.message || "An error occurred");
        console.error(ex);
      }
    });
  };

  useEffect(() => {
    if (props?.data?.id) {
      const category =
        props.data.unitCategory !== undefined &&
        props.data.unitCategory !== null
          ? props.data.unitCategory
          : UnitCategory.Simple;

      setUnitCategory(category);
      setSelectedType(props.data.type);

      if (category === UnitCategory.Compound) {
        if (props.data.compositeUnits)
          setCompositeUnits(props.data.compositeUnits);
      }

      const formData = {
        name: props.data.name,
        shortName: props.data.shortName,
        type: props.data.type,
        unitCategory: category,
        decimalPlaces: Number(props.data.decimalPlaces ?? 2),
      };
      form.setFieldsValue(formData);
    } else {
      setUnitCategory(UnitCategory.Simple);
      setCompositeUnits([]);
      setSelectedType(null);
      form.resetFields();
    }
  }, [props?.data]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (autoCompleteRef.current) {
        autoCompleteRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (currentClient?.id) fetchAllUnits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //   useEffect(() => {
  //     if (unitCategory === UnitCategory.Compound && selectedType !== undefined) {
  //       fetchChildCompoundUnits(selectedType!);
  //     }
  //   }, [unitCategory, selectedType]);

  const initialValues = {
    decimalPlaces: 2,
    unitCategory: UnitCategory.Simple,
  };

  EntryLevelKeys({
    save: () => postItem(),
    cancel: () => props.onCancel(false),
  });

  return (
    <Row>
      <Drawer
        open={props.open}
        title={props?.data?.id ? "Update Unit" : "Add Unit"}
        width={"700px"}
        onClose={() => props.onCancel(false)}
        closable={false}
        extra={
          <Space>
            <CloseOutlined onClick={() => props.onCancel(false)} />
          </Space>
        }
        footer={
          <Col style={{ textAlign: "right", paddingRight: 10 }}>
            <Tooltip
              title={<p>ALT+C</p>}
              placement="bottomLeft"
              open={showKeys}
            >
              <Button
                onClick={() => {
                  form.resetFields();
                  props.onCancel(false);
                }}
              >
                Cancel
              </Button>
            </Tooltip>

            <Tooltip
              title={<p>ALT+S</p>}
              placement="bottomLeft"
              open={showKeys}
            >
              <Button
                className="modelSaveButtonMargin"
                type="primary"
                onClick={() => postItem()}
                loading={saveButtonLoading}
              >
                {!props?.data?.id ? "Save" : "Update"}
              </Button>
            </Tooltip>
          </Col>
        }
      >
        <Spin spinning={loadingChildCompoundUnits}>
          <Form
            form={form}
            name="item"
            autoCapitalize="false"
            requiredMark={true}
            initialValues={initialValues}
            colon={false}
            labelAlign="left"
            labelWrap={true}
            layout="vertical"
          >
            <Row gutter={12}>
              <Col sm={24} md={12} lg={12}>
                {" "}
                <Form.Item
                  label="Unit Name"
                  name="name"
                  rules={[
                    { required: true, message: "Unit's name is required" },
                    {
                      pattern: /^[A-Za-z][A-Za-z0-9 ]{2,19}$/,
                      message:
                        "Only alphanumeric value having minimum 3 or maximum length 20 is allowed",
                    },
                    {
                      validator: (_, value, callback) => {
                        if (handleUnitNameDuplicate(value))
                          return Promise.reject("Unit name already exists");
                        else return Promise.resolve();
                      },
                    },
                  ]}
                  className="input-item"
                >
                  <Input
                    placeholder="Unit name (e.g., KILOGRAM)"
                    onInput={(e: any) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                    // onChange={handleDuplicate}
                    ref={autoCompleteRef}
                  />
                </Form.Item>
              </Col>
              <Col sm={24} md={12} lg={12}>
                <Form.Item
                  label="Type"
                  name="type"
                  rules={[{ required: true, message: "Type is required" }]}
                  className="input-item"
                >
                  <Select
                    placeholder="Select type"
                    options={UnitTypesOption}
                    onChange={handleTypeChange}
                  />
                </Form.Item>
              </Col>
              <Col sm={24} md={12} lg={12}>
                <Form.Item
                  rules={[
                    { required: true, message: "Short Name is required" },
                    {
                      pattern: /^[A-Za-z][A-Za-z0-9]{0,2}$/,
                      message:
                        "Must start with a letter and be up to 3 alphanumeric characters",
                    },
                    {
                      validator: (_, value, callback) => {
                        if (handleUnitShortNameDuplicate(value))
                          return Promise.reject(
                            "Unit's short name already exists"
                          );
                        else return Promise.resolve();
                      },
                    },
                  ]}
                  label={
                    <>
                      Short Name
                      <Tooltip
                        title="Maximum length is three characters"
                        placement="top"
                        color="#4b5b95"
                      >
                        <QuestionCircleOutlined style={{ marginLeft: "3px" }} />
                      </Tooltip>
                    </>
                  }
                  name="shortName"
                  className="input-item"
                >
                  <Input
                    placeholder="Short name (e.g., KG)"
                    maxLength={3}
                    onInput={(e: any) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                  />
                </Form.Item>
              </Col>
              <Col sm={24} md={12} lg={12}>
                <Form.Item
                  label={
                    <>
                      Decimal Places
                      <Tooltip
                        title="Number of decimal places for calculations (0-6)"
                        placement="top"
                        color="#4b5b95"
                      >
                        <QuestionCircleOutlined style={{ marginLeft: "3px" }} />
                      </Tooltip>
                    </>
                  }
                  name="decimalPlaces"
                  rules={[
                    { required: true, message: "Decimal places is required" },
                    {
                      type: "number",
                      min: 0,
                      max: 6,
                      message: "Decimal places must be between 0 and 6",
                    },
                  ]}
                  className="input-item"
                >
                  <InputNumber
                    placeholder="Please enter"
                    style={{ width: "100%" }}
                    min={0}
                    max={6}
                    precision={0}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label={
                <>
                  Unit Category
                  <Tooltip
                    title="Simple: Basic unit & Compound: Combination of multiple units (e.g., 1 Box = 12 Pieces)"
                    placement="top"
                    color="#4b5b95"
                  >
                    <QuestionCircleOutlined style={{ marginLeft: "3px" }} />
                  </Tooltip>
                </>
              }
              name="unitCategory"
              className="input-item"
            >
              <Radio.Group
                onChange={(e) => handleCategoryChange(e.target.value)}
                value={unitCategory}
              >
                {UnitCategoryOptions.map((opt) => (
                  <Radio key={opt.value} value={opt.value}>
                    {opt.label}
                  </Radio>
                ))}
              </Radio.Group>
            </Form.Item>

            {unitCategory === UnitCategory.Compound && (
              <Col style={{ marginBottom: 16 }}>
                <Row
                  justify="space-between"
                  align="middle"
                  style={{ marginBottom: 8 }}
                >
                  <Col>
                    <strong>Composite Units</strong>
                    <Tooltip
                      title="Define the units that make up this compound unit (e.g., 1 Box = 12 Pieces)"
                      placement="top"
                      color="#4b5b95"
                    >
                      <QuestionCircleOutlined style={{ marginLeft: "6px" }} />
                    </Tooltip>
                  </Col>
                  <Col>
                    <Tooltip title="Please select unit type and unit category to enable it">
                      <Button
                        type="dashed"
                        onClick={addCompositeUnit}
                        icon={<PlusOutlined />}
                        size="small"
                        disabled={
                          selectedType === null ||
                          selectedType === undefined ||
                          loadingChildCompoundUnits
                        }
                      >
                        Add Unit
                      </Button>
                    </Tooltip>
                  </Col>
                </Row>
                <Table
                  size="small"
                  columns={compositeColumns}
                  dataSource={compositeUnits}
                  pagination={false}
                  rowKey={(_, index) => index!}
                  locale={{ emptyText: "No composite units added" }}
                />
              </Col>
            )}
          </Form>
        </Spin>
      </Drawer>
    </Row>
  );
};

export default UnitModel;
